import React, { useEffect, useState } from 'react';
import { ScalePage as PageType, UserProfile } from '../../types/quiz';
import '../../assets/scss/quiz-editorial.scss';

interface Props {
    page: PageType;
    userProfile: UserProfile;
    onAnswer: (value: string) => void;
}

// Health spectrum. It can run left→right or right→left per question.
const STOPS = [
    [239, 68, 68], // #ef4444
    [245, 158, 11], // #f59e0b
    [34, 197, 94], // #22c55e
];
const colorAt = (t: number): [number, number, number] => {
    const seg = t * (STOPS.length - 1);
    const i = Math.min(Math.floor(seg), STOPS.length - 2);
    const f = seg - i;

    return [0, 1, 2].map((k) => Math.round(STOPS[i][k] + (STOPS[i + 1][k] - STOPS[i][k]) * f)) as [
        number,
        number,
        number,
    ];
};

/**
 * Horizontal rating scale, CliftonStrengths-style: an anchor statement on the
 * left and right, with N selectable points in between that grow toward the
 * ends. Tapping a point records the answer and auto-advances (like single
 * choice). Works for 5-point Likert and the 1–10 life-congruence slider alike.
 */
const ScalePage: React.FC<Props> = ({ page, userProfile, onAnswer }) => {
    const savedValue = page.data_field ? userProfile[page.data_field] : undefined;
    const initialSelected =
        typeof savedValue === 'string' || typeof savedValue === 'number' ? String(savedValue) : null;
    const [selected, setSelected] = useState<string | null>(initialSelected);
    const options = page.options || [];
    const n = options.length;
    const center = (n - 1) / 2;

    useEffect(() => {
        setSelected(initialSelected);
    }, [page.id, initialSelected]);

    const sizeFor = (i: number) => {
        const d = center === 0 ? 0 : Math.abs(i - center) / center; // 0 (middle) → 1 (ends)

        return Math.round(26 + d * 18); // 26px middle … 44px ends
    };

    const handleSelect = (value: string) => {
        setSelected(value);
        onAnswer(value);
    };

    return (
        <div className="page page--scale">
            <div className="page__container">
                <h2 className="page__question">{page.question}</h2>
                {page.subtitle && <p className="page__subtitle">{page.subtitle}</p>}

                {/* anchor statements */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        margin: '28px 0 14px',
                        fontSize: 14,
                        lineHeight: 1.4,
                        color: 'rgba(232,230,224,0.75)',
                    }}
                >
                    <span style={{ flex: '1 1 0', textAlign: 'left' }}>{page.leftLabel}</span>
                    <span style={{ flex: '1 1 0', textAlign: 'right' }}>{page.rightLabel}</span>
                </div>

                {/* scale dots - colored by healthy direction, no connecting line */}
                <div style={{ padding: '12px 0 4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {options.map((option, i) => {
                            const size = sizeFor(i);
                            const isSelected = selected === option.value;
                            const position = n > 1 ? i / (n - 1) : 0.5;
                            const healthPosition = page.healthyAt === 'left' ? 1 - position : position;
                            const [r, g, b] = colorAt(healthPosition);
                            const solid = `rgb(${r}, ${g}, ${b})`;

                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    aria-label={option.label}
                                    title={option.label}
                                    style={{
                                        width: size,
                                        height: size,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: Math.max(11, Math.round(size * 0.34)),
                                        fontFamily: 'inherit',
                                        transition: 'all 0.15s ease',
                                        background: isSelected ? solid : `rgba(${r}, ${g}, ${b}, 0.12)`,
                                        border: `2px solid ${solid}`,
                                        color: isSelected ? '#fff' : solid,
                                        boxShadow: isSelected ? `0 0 14px rgba(${r}, ${g}, ${b}, 0.55)` : 'none',
                                        transform: isSelected ? 'scale(1.12)' : 'none',
                                        fontWeight: isSelected ? 600 : 400,
                                    }}
                                >
                                    {option.value}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScalePage;
